import 'signer-app/screenreaders.module.css';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { FileRejection } from 'react-dropzone';
import { Box } from '@dropbox/dig-foundations';

import * as signatureTypes from 'signer-app/signature-modal/constants/signature-types';
import SignatureEditArea from 'signer-app/signature-modal/upload/signature-edit-area';
import { Bounds } from 'signer-app/signature-modal/upload/croppable';
import {
  SignatureModalContext,
  UploadedSignatureData,
} from 'signer-app/signature-modal/signature-modal-context/context';
import { ErrorBanner } from 'signer-app/utils/error-handling/error-banner';
import { CONTRAST_MID_VALUE } from './upload-constants';
import { validateUploadFile } from './validate-upload-file';
import { SignatureUploadDropzone } from './signature-upload-dropzone';

export { MAX_FILE_SIZE } from './upload-constants';

export interface UploadNewProps {
  // All state is managed locally - minimal props needed
  getSignatureUrl: SignatureModalContext['getSignatureUrl'];
  uploadSignature: (file: File) => Promise<UploadedSignatureData>;
  enableInsertButtonCallback: (enable: boolean) => void;
  isMobile: boolean;
  isNewSignerApp?: boolean;
}

/**
 * Upload component with local state management (useLocalState always true).
 * Functional component with hooks - manages signature state locally.
 */
export const UploadNew = React.forwardRef<any, UploadNewProps>((props, ref) => {
  const {
    getSignatureUrl,
    uploadSignature,
    enableInsertButtonCallback,
    isMobile,
    isNewSignerApp,
  } = props;

  const uploadButtonRef = useRef<HTMLButtonElement>(null);

  const [localUploadedSignature, setLocalUploadedSignature] =
    useState<UploadedSignatureData | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [showFileSizeError, setShowFileSizeError] = useState(false);
  const [showFileFormatError, setShowFileFormatError] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [contrast, setContrast] = useState(CONTRAST_MID_VALUE);
  const [bounds, setBounds] = useState<Bounds | null>(null);

  useEffect(() => {
    enableInsertButtonCallback(!!localUploadedSignature);
  }, [localUploadedSignature, enableInsertButtonCallback]);

  const handleUpload = useCallback(
    async (file: File) => {
      setIsUploading(true);
      setError(null);

      try {
        const result = await uploadSignature(file);
        setLocalUploadedSignature(result);
        setRotation(result.rotation ?? 0);
        setContrast(result.contrast ?? CONTRAST_MID_VALUE);
        setBounds(result.bounds ?? null);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsUploading(false);
      }
    },
    [uploadSignature],
  );

  const handleDropAccepted = useCallback(
    (files: File[]) => {
      setShowFileSizeError(false);
      setShowFileFormatError(false);
      if (files.length > 0) {
        handleUpload(files[0]);
      }
    },
    [handleUpload],
  );

  const handleDropRejected = useCallback((rejections: FileRejection[]) => {
    if (rejections.length > 0) {
      const file = rejections[0].file;
      const { showFileSizeError, showFileFormatError } =
        validateUploadFile(file);
      setShowFileSizeError(showFileSizeError);
      setShowFileFormatError(showFileFormatError);
    }
  }, []);

  const handleClear = useCallback(() => {
    setLocalUploadedSignature(null);
    setRotation(0);
    setContrast(CONTRAST_MID_VALUE);
    setBounds(null);
    setError(null);
    uploadButtonRef.current?.focus();
  }, []);

  const handlePreviewChange = useCallback(
    (
      changes: Partial<{
        bounds: Bounds | null;
        rotation: number;
        contrast: number;
      }>,
    ) => {
      if (changes.bounds !== undefined) {
        setBounds(changes.bounds);
      }
      if (changes.rotation !== undefined) {
        setRotation(changes.rotation);
      }
      if (changes.contrast !== undefined) {
        setContrast(changes.contrast);
      }
    },
    [],
  );

  const getSignatureData = useCallback(() => {
    const signature = localUploadedSignature;

    if (signature === null) {
      return {};
    }

    const signatureData: Record<string, unknown> = {
      create_type_code: signatureTypes.UPLOAD,
      guid: signature.guid,
      threshold: contrast,
      degrees: rotation,
    };

    if (bounds) {
      Object.assign(signatureData, {
        is_cropped: true,
        x: bounds.left,
        y: bounds.top,
        width: bounds.width,
        height: bounds.height,
      });
    } else {
      Object.assign(signatureData, {
        width: signature.width,
        height: signature.height,
      });
    }

    return signatureData;
  }, [localUploadedSignature, contrast, rotation, bounds]);

  React.useImperativeHandle(
    ref,
    () => ({
      getSignatureData,
    }),
    [getSignatureData],
  );

  return (
    <div aria-busy={isUploading}>
      {error && (
        <Box marginBottom={'Micro Small'}>
          <ErrorBanner error={error} onClose={() => setError(null)} />
        </Box>
      )}
      {!isUploading && localUploadedSignature ? (
        <SignatureEditArea
          getSignatureUrl={getSignatureUrl}
          signature={localUploadedSignature}
          clearUploadedSignature={handleClear}
          onPreviewChange={handlePreviewChange}
          contrast={contrast}
          rotation={rotation}
          isMobile={isMobile}
        />
      ) : (
        <SignatureUploadDropzone
          isUploading={isUploading}
          showFileSizeError={showFileSizeError}
          showFileFormatError={showFileFormatError}
          isNewSignerApp={isNewSignerApp}
          uploadButtonRef={uploadButtonRef}
          onDropAccepted={handleDropAccepted}
          onDropRejected={handleDropRejected}
        />
      )}
    </div>
  );
});

UploadNew.displayName = 'UploadNew';

export default UploadNew;
